import { useCallback } from "react";
import { VictoryChart, VictoryBar, VictoryAxis } from "victory";

import { ContentCard } from "@narmi/design_system";
import { modules } from "byzantine";

import type { DataPoint } from "byzantine/src/dbbl/businessLogic/entities/subscriptions";

import styles from "./SubscriptionsChart.module.scss";

interface SubscriptionsChartProps {
  data: DataPoint[];
  monthLabels: Record<string, string>;
  selectedMonth: string;
  prevMonth: MonthString | null;
  nextMonth: MonthString | null;
  selectedMonthFullName: string;
  year: string;
  isSelectedThisMonth: boolean;
  prev12MonthsTotal: number;
  monthTotal: number;
  onMonthChange: (month: MonthString) => void;
  screenSize: keyof typeof modules.subscriptions.webConfigs;
}

const SubscriptionsChart = ({
  data,
  monthLabels,
  selectedMonth,
  prevMonth,
  nextMonth,
  selectedMonthFullName,
  year,
  isSelectedThisMonth,
  prev12MonthsTotal,
  monthTotal,
  onMonthChange,
  screenSize,
}: SubscriptionsChartProps) => {
  const config = modules.subscriptions.webConfigs[screenSize];
  const formatTick = useCallback(
    (d: unknown) => {
      const label = String(d);
      const key = label.slice(5);
      return monthLabels[key] ?? label;
    },
    [monthLabels]
  );

  const handleBarClick = useCallback(
    (_: unknown, props: { datum: { month: MonthString } }) =>
      onMonthChange(props.datum.month),
    [onMonthChange]
  );

  return (
    <div className={`${styles.container} rounded--all padding--all--m`}>
      {/* Navigation */}
      <div className={styles.navigationContainer}>
        <div className={styles.selectedMonth}>
          {selectedMonthFullName} {year}
        </div>
        <div className={styles.navigation}>
          <button
            name="chevron-left"
            className={styles.directionBtn}
            disabled={!prevMonth}
            onClick={() => prevMonth && onMonthChange(prevMonth)}
          >
            <span className="narmi-icon-chevron-left" />
          </button>
          <button
            name="chevron-right"
            className={styles.directionBtn}
            disabled={!nextMonth}
            onClick={() => nextMonth && onMonthChange(nextMonth)}
          >
            <span className="narmi-icon-chevron-right" />
          </button>
        </div>
      </div>

      {/* Summary */}
      <ContentCard kind="bordered">
        <div className={styles.summary}>
          <div>
            <div className={styles.summaryValue}>
              ${prev12MonthsTotal.toFixed(2)}
            </div>
            <div className="fontSize--xs fontColor--secondary">
              Previous 12 months
            </div>
          </div>
          <div className={styles.rightSummary}>
            <div className={styles.summaryValue}>${monthTotal.toFixed(2)}</div>
            <div className="fontSize--xs fontColor--secondary">
              {selectedMonthFullName}{" "}
              {isSelectedThisMonth ? "(This month)" : ""}
            </div>
          </div>
        </div>

        <VictoryChart
          height={config.height}
          padding={config.padding}
          domain={{
            x: [0, data.length + 1],
            y: [
              0,
              data.length
                ? Math.max(...data.map((datum: DataPoint) => datum.total)) *
                  config.yFactor
                : 0,
            ],
          }}
          domainPadding={{ x: config.domainPaddingX }}
        >
          {/* Hide left axis */}
          <VictoryAxis
            dependentAxis
            style={{
              axis: { stroke: "transparent" },
              ticks: { stroke: "transparent" },
              tickLabels: { fill: "transparent" },
            }}
          />
          {/* Bottom axis labels only */}
          <VictoryAxis
            tickValues={data.map((datum: DataPoint) => datum.month)}
            tickFormat={formatTick}
            style={{
              axis: { stroke: "transparent" },
              ticks: { stroke: "transparent" },
              tickLabels: {
                fontSize: config.fontSize.tick,
                padding: config.tickPadding,
              },
            }}
          />
          <VictoryBar
            data={data}
            x="month"
            y="total"
            barWidth={config.barWidth}
            cornerRadius={config.cornerRadius}
            style={{
              data: {
                fill: ({ datum }) =>
                  datum.month === selectedMonth
                    ? "var(--theme-primary)"
                    : "var(--bgColor-cloudGrey)",
              },
            }}
            events={[
              { target: "data", eventHandlers: { onClick: handleBarClick } },
            ]}
          />
        </VictoryChart>
      </ContentCard>
    </div>
  );
};

export default SubscriptionsChart;
